import { Injectable } from "@angular/core";
import { UserManager, User } from "oidc-client-ts";
import { BehaviorSubject, Observable } from "rxjs";
import { config } from "../../oidc/oidc-config";
import { StorfaDefnyddiwrService } from "../system/storfa-defnyddiwr.service";
import { IaithService } from "../iaith/iaith.service";

@Injectable({
  providedIn: "root",
})
export class DilysuService {
  private userManager: UserManager;

  // Defnyddiwr
  private defnyddiwrSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  public readonly defnyddiwr$: Observable<User | null> = this.defnyddiwrSubject.asObservable();

  // Stats llwytho defnyddiwr
  private defnyddiwrWediLlwythoSubject = new BehaviorSubject<boolean>(false);
  public readonly defnyddiwrWediLlwytho$ = this.defnyddiwrWediLlwythoSubject.asObservable();

  // Statws silent refresh
  private adnewydduSubject = new BehaviorSubject<boolean>(false);
  public readonly adnewyddu$ = this.adnewydduSubject.asObservable();

  constructor(
    private storfaDefnyddiwrService: StorfaDefnyddiwrService,
    private iaithService: IaithService,
  ) {
    this.userManager = new UserManager(config);

    this.llwythoDefnyddiwr();

    this.userManager.events.addUserLoaded((user) => {
      this.defnyddiwrSubject.next(user);

      this.arbedProffilYnLocalStorage(user);

      this.adnewydduSubject.next(false);
    });

    this.userManager.events.addUserUnloaded(() => {
      this.defnyddiwrSubject.next(null);

      this.storfaDefnyddiwrService.tynnuEitem("proffil");

      this.adnewydduSubject.next(false);
    });

    this.userManager.events.addAccessTokenExpired(() => {
      this.adnewydduSubject.next(true);

      console.warn("Tocyn mynediad wedi dod i ben, felly silent refresh yn cychwyn.");

      this.trinTocynYnDodIBen();
    });

    this.userManager.events.addSilentRenewError(() => {
      console.warn("Methu neud silent renew, felly yn allgofnodi.");

      this.adnewydduSubject.next(false);

      this.allgofnodi();
    });
  }

  arbedProffilYnLocalStorage(user: User): void {
    this.storfaDefnyddiwrService.tynnuEitem("proffil");

    const proffil = {
      id: user.profile.sub,
      enw: user.profile.name,
      tenant: user.profile.tenant,
      rol: user.profile.role,
      tocyn: user.access_token,
      iaith: user.profile.iaith,
      rhwystrocaugwaith: user.profile.rhwystrocaugwaith,
      modiwlau: user.profile.modiwlau,
    };

    this.storfaDefnyddiwrService.setio("proffil", JSON.stringify(proffil));

    this.iaithService.initializeIaith();
  }

  public async llwythoDefnyddiwr(): Promise<void> {
    // Cychwyn drwy glirio hen legacy tokens
    this.clirioHenTokens();

    try {
      const user = await this.userManager.getUser();

      if (user && !user.expired) {
        this.defnyddiwrSubject.next(user);
      } else {
        this.defnyddiwrSubject.next(null);
      }
    } catch (e) {
      console.error("Methiant llwytho defnyddiwr", e);
      this.defnyddiwrSubject.next(null);
    } finally {
      // Llwytho wedi gorffen
      this.defnyddiwrWediLlwythoSubject.next(true);
    }
  }

  private async trinTocynYnDodIBen() {
    try {
      const newUser = await this.userManager.signinSilent();
      this.defnyddiwrSubject.next(newUser);
    } catch (e) {
      console.error("Methiant silent renew", e);
      this.allgofnodi();
    }
  }

  /**
   * Method i cychwyn y mewngofnodi
   * @returns URL i pori ar ôl mewngofnodi
   */
  mewngofnodi(): Promise<void> {
    this.userManager.clearStaleState();

    this.storfaDefnyddiwrService.tynnuEitem("proffil");

    return this.userManager.signinRedirect();
  }

  /**
   * Method i mewngofnodi gyda'r oidc-client ar ôl derbyn ateb yn nôl o'r identity
   * @returns
   */
  async dilysu(): Promise<User | null> {
    const user = await this.userManager.signinRedirectCallback();

    this.defnyddiwrSubject.next(user);

    return user;
  }

  /**
   *
   * @returns Gwirio os mae'r defnyddiwr dal gyda sesiwn dilys
   */
  wediMewngofnodi(): boolean {
    let user = this.nolDefnyddiwr();

    return user && user.access_token && !user.expired;
  }

  nolDefnyddiwr(): User | null {
    return this.defnyddiwrSubject.getValue();
  }

  nolTocynMynediad(): string | null {
    const user = this.defnyddiwrSubject.getValue();

    if (user && user.token_type && user.access_token && !user.expired) {
      return `${user.token_type} ${user.access_token}`;
    }

    return null;
  }

  // Gwirio os defnyddiwr wedi ei ddilysu
  defnyddiwrWediDdilysu(): boolean {
    const user = this.defnyddiwrSubject.getValue();

    return !!(user && !user.expired);
  }

  /**
   *
   * @returns Method cychwyn y broses allgofnodi
   */
  allgofnodi(): Promise<any> {
    this.defnyddiwrSubject.next(null);
    this.userManager.removeUser();
    return this.userManager.signoutRedirect();
  }

  /**
   * Method i allgofnodi gyda'r oidc-client ar ôl derbyn ateb yn nôl o'r identity
   * @returns
   */
  allgofnodiAilgyfeirio(): Promise<any> {
    return this.userManager.signoutRedirectCallback();
  }

  /**
   * Method clirio unrhyw ddata yn y local storage sydd dros ben
   */
  clirioDefnyddiwr(): any {
    this.userManager.removeUser();

    this.storfaDefnyddiwrService.tynnuEitem("proffil");

    this.userManager.clearStaleState();

    this.clirioHenTokens();
  }

  /**
   * Method i glirio hen tokens
   */
  clirioHenTokens(): any {
    this.storfaDefnyddiwrService.tynnuEitem("proffil", true);

    this.storfaDefnyddiwrService.tynnuEitem("iaith", true);

    this.storfaDefnyddiwrService.tynnuEitem("urlReturn", true);

    this.storfaDefnyddiwrService.tynnuEitem("urlReturn");
  }
}
